// Copyright (c) 2026 Nathan Blackman / Find My Trout. All rights reserved.

import React, { useState, useEffect } from 'react';
import { X, Pencil, Trash2 } from 'lucide-react';
import { collection, query, where, getDocs, addDoc, doc, updateDoc, deleteDoc, orderBy, limit, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';

const StockingReportsModal = ({ isOpen, onClose, streamId, streamName }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newReportText, setNewReportText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingReportId, setEditingReportId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const { currentUser, userProfile } = useAuth();

  useEffect(() => {
    if (isOpen && streamId) {
      loadReports();
    }
  }, [isOpen, streamId]);

  const loadReports = async (fromServer = false) => {
    if (!streamId) return;
    if (!fromServer) setLoading(true);
    try {
      const q = query(
        collection(db, 'stockingReports'),
        where('streamId', '==', streamId),
        orderBy('createdAt', 'desc'),
        limit(20)
      );
      const options = fromServer ? { source: 'server' } : undefined;
      const snapshot = await getDocs(q, options);
      const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setReports(list);
    } catch (error) {
      console.error('Error loading stocking reports:', error);
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const text = (newReportText || '').trim();
    if (!text || !currentUser || !streamId) return;
    setSubmitting(true);
    try {
      const docRef = await addDoc(collection(db, 'stockingReports'), {
        streamId,
        streamName: streamName || '',
        userId: currentUser.uid,
        userDisplayName: userProfile?.displayName || currentUser.displayName || 'Angler',
        text,
        createdAt: serverTimestamp()
      });
      setNewReportText('');
      const displayName = userProfile?.displayName || currentUser.displayName || 'Angler';
      const optimisticReport = {
        id: docRef.id,
        streamId,
        streamName: streamName || '',
        userId: currentUser.uid,
        userDisplayName: displayName,
        text,
        createdAt: { toDate: () => new Date() }
      };
      setReports(prev => [optimisticReport, ...prev]);
      await loadReports(true);
    } catch (error) {
      console.error('Error posting stocking report:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (report) => {
    setEditingReportId(report.id);
    setEditingText(report.text || '');
  };

  const handleCancelEdit = () => {
    setEditingReportId(null);
    setEditingText('');
  };

  const handleSaveEdit = async () => {
    const text = (editingText || '').trim();
    if (!editingReportId || !text) return;
    setSavingId(editingReportId);
    try {
      await updateDoc(doc(db, 'stockingReports', editingReportId), { text });
      setReports(prev => prev.map(r => r.id === editingReportId ? { ...r, text } : r));
      setEditingReportId(null);
      setEditingText('');
    } catch (error) {
      console.error('Error updating stocking report:', error);
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (report) => {
    if (!window.confirm('Delete this stocking report?')) return;
    setDeletingId(report.id);
    try {
      await deleteDoc(doc(db, 'stockingReports', report.id));
      setReports(prev => prev.filter(r => r.id !== report.id));
    } catch (error) {
      console.error('Error deleting stocking report:', error);
    } finally {
      setDeletingId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="stocking-reports-modal stocking-reports-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="stocking-reports-modal-header">
          <div>
            <h2 className="modal-title">Stocking Reports</h2>
            <p className="modal-subtitle">{streamName}</p>
          </div>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Close">
            <X size={24} />
          </button>
        </div>

        <div className="stocking-reports-modal-body">
          {loading ? (
            <div className="stocking-reports-loading">
              <div className="spinner-small" />
              <p>Loading reports...</p>
            </div>
          ) : (
            <>
              <h3 className="stocking-reports-list-title">Community updates</h3>
              <div className="stocking-reports-list">
                {reports.length === 0 ? (
                  <p className="stocking-empty-msg">No stocking reports yet. Be the first to share an update!</p>
                ) : (
                  reports.map((r) => {
                    const date = r.createdAt?.toDate ? r.createdAt.toDate() : (r.createdAt ? new Date(r.createdAt) : null);
                    const dateStr = date ? date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';
                    const isOwner = currentUser && r.userId === currentUser.uid;
                    const isEditing = editingReportId === r.id;
                    return (
                      <div key={r.id} className="stocking-report-card">
                        <div className="stocking-report-card-meta">
                          <span className="stocking-report-author">{r.userDisplayName || 'Angler'}</span>
                          <span className="stocking-report-date">{dateStr}</span>
                          {isOwner && !isEditing && (
                            <span className="stocking-report-card-actions">
                              <button
                                type="button"
                                className="stocking-report-action-btn"
                                onClick={() => handleEdit(r)}
                                title="Edit"
                                disabled={!!deletingId}
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                type="button"
                                className="stocking-report-action-btn stocking-report-action-btn-delete"
                                onClick={() => handleDelete(r)}
                                title="Delete"
                                disabled={!!deletingId || deletingId === r.id}
                              >
                                {deletingId === r.id ? (
                                  <span className="stocking-report-deleting">...</span>
                                ) : (
                                  <Trash2 size={14} />
                                )}
                              </button>
                            </span>
                          )}
                        </div>
                        {isEditing ? (
                          <div className="stocking-report-edit">
                            <textarea
                              className="stocking-report-edit-textarea"
                              value={editingText}
                              onChange={(e) => setEditingText(e.target.value)}
                              rows={3}
                              disabled={savingId === r.id}
                            />
                            <div className="stocking-report-edit-buttons">
                              <button
                                type="button"
                                className="stocking-report-edit-cancel"
                                onClick={handleCancelEdit}
                                disabled={!!savingId}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                className="stocking-report-edit-save"
                                onClick={handleSaveEdit}
                                disabled={!!savingId || !editingText.trim()}
                              >
                                {savingId === r.id ? 'Saving...' : 'Save'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="stocking-report-card-text">{r.text}</div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {currentUser ? (
                <form onSubmit={handleSubmit} className="stocking-reports-form">
                  <label htmlFor="stocking-report-input" className="form-label">Add a report</label>
                  <textarea
                    id="stocking-report-input"
                    className="form-textarea stocking-reports-textarea"
                    placeholder="Share stocking info (e.g. date, species, location)..."
                    value={newReportText}
                    onChange={(e) => setNewReportText(e.target.value)}
                    rows={3}
                    disabled={submitting}
                  />
                  <button type="submit" className="btn-submit" disabled={submitting || !newReportText.trim()}>
                    {submitting ? 'Posting...' : 'Post report'}
                  </button>
                </form>
              ) : (
                <p className="stocking-sign-in-msg">Sign in to add a stocking report.</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StockingReportsModal;
